import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { zodResolver } from "@hookform/resolvers/zod"
import { AlertDialogProps } from "@radix-ui/react-alert-dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { DetailCheckbox } from "@/components/ui/checkbox"
import { CareProvider } from "@/modules/care-provider/components/edit-care-provider/care-provider-practice-tab"
import {
  ReportToCloseSchema,
  reportToCloseSchema,
} from "@/modules/care-provider/components/edit-care-provider/schema/care-provider-schema"
import { combineCareProviderTitle } from "@/modules/care-provider/utils/combine-care-provider-title"

interface CareProviderCloseDialogProps extends AlertDialogProps {
  careProvider: CareProvider
  onDiscard?(event: React.MouseEvent<HTMLElement>): void
  onConfirm?(): void
}

const CareProviderCloseDialog = ({
  onDiscard,
  onConfirm,
  careProvider,
  ...otherProps
}: CareProviderCloseDialogProps) => {
  const { t } = useTranslation()

  const form = useForm<ReportToCloseSchema>({
    resolver: zodResolver(reportToCloseSchema),
    defaultValues: {
      confirmed: false,
    },
    reValidateMode: "onChange",
  })

  const {
    formState: { errors: formErrors },
  } = form

  const onSubmit = () => {
    onConfirm?.()
  }

  const handleSave = () => form.handleSubmit(onSubmit)()

  const name = combineCareProviderTitle({
    businessName: careProvider?.businessName,
    title: careProvider?.title,
    firstName: careProvider?.firstName,
    lastName: careProvider?.lastName,
  })

  return (
    <AlertDialog {...otherProps} data-testid="care-provider-close-dialog">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <p className="text-on-surface text-xl font-bold">
              {t("admin.careProviders.dialog.reportToClose.confirmationTitle", {
                name,
              })}
            </p>
          </AlertDialogTitle>
          <AlertDialogDescription className="text-on-surface-subdued grid gap-6">
            <div>{t("admin.careProviders.dialog.reportToClose.closeConfirmationMessage")}</div>
            <div className="grid gap-2">
              <DetailCheckbox
                error={!!formErrors.confirmed}
                checked={form.getValues("confirmed")}
                onCheckedChange={(checked: boolean) => {
                  form.setValue("confirmed", checked, {
                    shouldValidate: true,
                  })
                }}>
                {t("admin.careProviders.dialog.reportToClose.closeConfirmationCheckbox")}
              </DetailCheckbox>
              {formErrors.confirmed && (
                <div>
                  <span className="text-body-sm text-on-critical-surface items-center">
                    {t(`${formErrors.confirmed.message}`)}
                  </span>
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onDiscard}>
            {t("patient.agenda.timeWindowForm.cancelConfirmationContinueLabel")}
          </AlertDialogCancel>
          <AlertDialogAction className="bg-critical" onClick={handleSave}>
            {t("admin.careProviders.dialog.reportToClose.reportAsClosedButtonTitle")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default CareProviderCloseDialog
